/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlexContainer,
  GridContainer,
  Label,
  useDeviceSize,
  useToast,
  Icon,
  IconName,
  InphiniteThemeColors,
} from '@facephi/ui-react';
import JSZip from 'jszip';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useDownloadBase64, useLoadAsset, useVariables } from '../../hooks';
import { useOperation, useData } from '../../providers';
import {
  Asset,
  DOCUMENT_FACIAL,
  DOCUMENT_SELFIE,
  RoutesName,
  StatusTypeList,
  circleIconData,
  GovernmentCountryCodes,
  internalProviders,
  StatusOperations,
  SseAssetsTypes,
  MimeType,
  FacialAuthenticationStatus,
  COUNTRY_NOT_SUPPORTED,
  FINISH_TIMEOUT,
  PLATFORM_SUBDIRECTORY,
  CORRESPONDENCE_THRESHOLD,
} from '../../states';
import {
  antifraudState,
  documentValidationState,
  logImagesState,
  previousOnboardingsValidationState,
  signState,
  signTypeState,
  governmentValidationState,
  livenessValidationState,
  newOperationIdState,
  recordOnboardingState,
} from '../../states/atoms';

import { flattenDeepObject } from '../../utils';
import {
  CardGridSmall,
  CardResults,
  CardResultsContent,
  CardResultsHeaderSpinner,
} from '../cards';
import { CardList, DataList } from '../cards/CardList';
import { ModalSign } from '../sign/ModalSign';
import { Banner } from './Banner';
import { ImageLoad } from './ImageLoad';
import { ResultsHeader } from './ResultHeader';
import { SelfieValidations } from './SelfieValidation';
import { StatusIcon, StatusTypeProps } from './StatusIcon';
import {
  ButtonLinkStyles,
  MultipleProvidersDropdown,
  ResultsButton,
  ResultsDataImg,
  ResultsDataImgWrapper,
  ResultsDataWrapper,
  ResultsWrapper,
} from './Styles';

type DownloadImages = {
  name: string;
  url: string;
};

const getLabelColor = (status: StatusTypeProps | undefined) => {
  let color = 'grey700';
  switch (status) {
    case StatusOperations.SUCCEEDED:
      color = 'success400';
      break;
    case StatusOperations.DENIED:
      color = 'error400';
      break;
    case StatusOperations.CONNECTION_ERROR:
    case StatusOperations.CHECK_PLATFORM:
      color = 'warning400';
      break;
    default:
      color = 'grey700';
  }

  return color;
};

export const onboardingIconNew = (
  statusType?: StatusTypeProps
): React.ReactElement | undefined => {
  let iconName;
  let iconColor;
  switch (statusType) {
    case StatusTypeList.SUCCEEDED:
      iconName = circleIconData['success'].iconName;
      iconColor = circleIconData['success'].iconColor;
      break;

    case StatusTypeList.DENIED:
      iconName = circleIconData['error'].iconName;
      iconColor = circleIconData['error'].iconColor;
      break;

    case StatusTypeList.PENDENT:
    case StatusOperations.CONNECTION_ERROR:
    case StatusOperations.EXPIRED:
    case StatusOperations.CHECK_PLATFORM:
      iconName = circleIconData['warning'].iconName;
      iconColor = circleIconData['warning'].iconColor;
      break;

    case StatusTypeList.IN_PROGRESS:
    case StatusTypeList.STARTED:
      iconName = undefined;
      iconColor = undefined;
      break;
  }
  return iconName ? (
    <Icon
      iconName={iconName}
      color={iconColor}
      size="32"
      weight="fill"
      testId="onboarding_card_result_icon"
      className="onboarding_card_result_icon"
    />
  ) : (
    <CardResultsHeaderSpinner genericSpinner size="S" insideComponent />
  );
};

export const Results = () => {
  const {
    idCapturedState,
    selfieCapturedState,
    facialAuthenticationState,
    passiveLivenessState,
    idValidatedState,
    civilRegistryState,
    previousOnboardingState,
    operationFinishedState,
    documentSignatureState,
    personalInformationState,
    dispatchDocumentSignature,
    resetStates,
    setIsOperationFinished,
    disconnect,
    connect: connectSSE,
    getOperationResults,
    clearPolling,
    timeoutError,
    reloadError,
  } = useData();

  const { workflow, opreationAssets, antispoofResult } = useOperation();
  const { t } = useTranslation();
  const { isMobile } = useDeviceSize();
  const navigate = useNavigate();
  const { operationId } = useParams();
  const { appTesting, isCanada, platformUrl } = useVariables();
  const { getImageUrl } = useLoadAsset();
  const { download } = useDownloadBase64();
  const { toastManager } = useToast();

  const antifraud = useRecoilValue(antifraudState);
  const documentValidation = useRecoilValue(documentValidationState);
  const previousOnboardingsValidation = useRecoilValue(
    previousOnboardingsValidationState
  );
  const governmentValidation = useRecoilValue(governmentValidationState);
  const logImages = useRecoilValue(logImagesState);
  const sign = useRecoilValue(signState);
  const signType = useRecoilValue(signTypeState);
  const [newOperationId, setNewOperationId] =
    useRecoilState(newOperationIdState);
  const recordOnboarding = useRecoilValue(recordOnboardingState);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [signatureAttemps, setSignatureAttemps] = useState<number>(0);
  const [unifiedAssets, setUnifiedAssets] = useState<ReactNode>();
  const [showGovernmentValidation, setShowGovernmentValidation] =
    useState<boolean>(governmentValidation);
  const correspondenceSidesSetted = useRef<StatusTypeList | undefined>();
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const platformRedirectUrl = `${platformUrl}${PLATFORM_SUBDIRECTORY}/${newOperationId}`;

  const biometricValidationLoading = livenessValidationState
    ? selfieCapturedState.loading ||
      facialAuthenticationState.loading ||
      passiveLivenessState.loading
    : selfieCapturedState.loading || facialAuthenticationState.loading;

  const viewSignButton: boolean = useMemo(
    () =>
      operationFinishedState?.status === StatusOperations.SUCCEEDED &&
      sign &&
      signType &&
      !!workflow,
    [operationFinishedState]
  );

  const disableSignButton =
    documentSignatureState.loading ||
    documentSignatureState.data?.signed ||
    signatureAttemps >= 3;

  const documentSignatureStatus = documentSignatureState.loading
    ? StatusTypeList.STARTED
    : documentSignatureState.status === StatusTypeList.STARTED
    ? StatusTypeList.PENDENT
    : documentSignatureState.status === StatusTypeList.SUCCEEDED
    ? StatusTypeList.SAVED
    : documentSignatureState.status;

  const canCheckPlatform = !isCanada && reloadError && !isMobile;

  const canadaMorphologicalMessage = useMemo(
    () =>
      idValidatedState.loading
        ? 'Morphological'
        : 'Morphological validation performed',
    [idValidatedState]
  );

  const selfieValidationAssets: Asset[] | undefined = useMemo(() => {
    const assetsFromOperation = Array.from(opreationAssets || []);
    return assetsFromOperation;
  }, [opreationAssets]);

  const governementServiceIcon: IconName = useMemo(() => {
    switch (idCapturedState.data?.BACKSIDE?.MRZ_DATA?.ISSUING_COUNTRY) {
      case GovernmentCountryCodes.ARG:
        return 'RenaperIcon';
      case GovernmentCountryCodes.PER:
        return 'ReniecIcon';
      case GovernmentCountryCodes.BRA:
        return 'SerproIcon';
      default:
        return 'FingerprintSimple';
    }
  }, [idCapturedState]);

  const correspondenceSidesState: StatusTypeList | undefined = useMemo(() => {
    if (correspondenceSidesSetted.current)
      return correspondenceSidesSetted.current;

    if (idCapturedState?.data) {
      correspondenceSidesSetted.current =
        idCapturedState.data.SCORING?.OVERALL_SIDE_CORRESPONDENCE >
        CORRESPONDENCE_THRESHOLD
          ? StatusTypeList.CORRESPONDED
          : StatusTypeList.NOT_CORRESPONDED;
      return correspondenceSidesSetted.current;
    }
  }, [idCapturedState]);

  const canReloadResults: boolean = useMemo(() => {
    if (!operationFinishedState.loading && !reloadError && !isCanada) {
      return (
        operationFinishedState?.status === StatusOperations.CONNECTION_ERROR ||
        facialAuthenticationState?.status ===
          StatusOperations.CONNECTION_ERROR ||
        passiveLivenessState?.status === StatusOperations.CONNECTION_ERROR ||
        idCapturedState?.status === StatusOperations.CONNECTION_ERROR ||
        (previousOnboardingsValidation &&
          previousOnboardingState?.status ===
            StatusOperations.CONNECTION_ERROR) ||
        // antifraud && TO DO
        (showGovernmentValidation &&
          civilRegistryState?.status === StatusOperations.CONNECTION_ERROR) ||
        (documentValidation &&
          idValidatedState?.status === StatusOperations.CONNECTION_ERROR)
      );
    }
    return false;
  }, [operationFinishedState]);

  const outlineFinishButton =
    canReloadResults || canCheckPlatform || viewSignButton;

  const getLoadingDocuments = () =>
    Array.from({ length: 4 }).map((_, index) => (
      <ResultsDataImg key={index}>
        <Skeleton height={70} width={70} borderRadius={8} />
        <div style={{ marginTop: '0.8rem' }}>
          <Skeleton width="65%" height={16} borderRadius={8} />
        </div>
      </ResultsDataImg>
    ));

  const cleanTimeout = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  };

  const handleFinish = () => {
    resetStates();
    setIsOperationFinished(false);
    setNewOperationId('');
    disconnect();
    cleanTimeout();
    clearPolling();
    if (!recordOnboarding) {
      navigate(RoutesName.home);
    } else {
      window.location.href = '/';
    }
  };

  const handleReload = () => {
    setSignatureAttemps(0);
    getOperationResults(newOperationId);
  };

  const handleVisitPlatform = () => {
    window.open(platformRedirectUrl, '_blank', 'noopener,noreferrer');
  };

  const downloadImages = async () => {
    const zip = new JSZip();
    try {
      const images = ['DOCUMENT_BACK', 'DOCUMENT_FRONT', 'SELFIE'].reduce(
        (acc, type) => {
          const image = opreationAssets.find((item) => item.type === type);
          if (image) {
            acc.push({
              name: type,
              url: image.url,
            });
          }
          return acc;
        },
        [] as DownloadImages[]
      );
      if (images.length) {
        const data = await Promise.all(
          images.map((item) => getImageUrl(item.url))
        );
        images.forEach((item, index: number) => {
          zip.file(
            `${item.name}.${data[index].type.split('/')[1]}`,
            data[index]
          );
        });
        const content = await zip.generateAsync({ type: 'blob' });
        download(URL.createObjectURL(content), `images_${operationId}`);
      }
    } catch {
      toastManager({
        duration: 3000,
        type: 'error',
        message: t(
          'Something went wrong with the download of images, try another time'
        ),
      });
    }
  };

  const transformPersonalInformation = (
    status: StatusTypeProps
  ): DataList[] => {
    const data = idCapturedState.data?.BACKSIDE.MRZ_DATA;
    const flattenData = flattenDeepObject(idCapturedState.data, false);
    const getInfo = (validKeys: RegExp, invalidKeys?: RegExp) =>
      Array.from(
        Object.keys(flattenData)
          .filter(
            (key) =>
              validKeys.test(key) &&
              !/(CHEKCS)/.test(key) &&
              !invalidKeys?.test(key)
          )
          .filter((key) => flattenData[key])
          .reduce((acc, key) => acc.add(flattenData[key]), new Set<string>())
      ).join(' ');

    const personalInformation = personalInformationState.data;
    return [
      {
        title: 'name',
        value:
          status === StatusOperations.CONNECTION_ERROR
            ? '-'
            : personalInformation?.name ||
              data?.NAME ||
              getInfo(/(NAME|NAMES)/) ||
              '-',
        idText: 'name',
      },
      {
        title: 'surname',
        value:
          status === StatusOperations.CONNECTION_ERROR
            ? '-'
            : personalInformation?.surname ||
              data?.SURNAME ||
              getInfo(/(SURNAME)/) ||
              '-',
        idText: 'surname',
      },
      {
        title: 'nationality',
        value:
          status === StatusOperations.CONNECTION_ERROR
            ? '-'
            : personalInformation?.nationality ||
              data?.ISSUING_COUNTRY ||
              getInfo(/(ISSUING_COUNTRY|NATIONALITY|COUNTRY)/) ||
              '-',
        idText: 'nationality',
      },
      {
        title: 'documentNumber',
        value:
          status === StatusOperations.CONNECTION_ERROR
            ? '-'
            : personalInformation?.documentNumber ||
              data?.PERSONAL_NUMBER ||
              getInfo(/(DOCUMENT_NUMBER|PERSONAL_NUMBER)/) ||
              '-',
        idText: 'document_number',
      },
      {
        title: 'birthDate',
        value:
          status === StatusOperations.CONNECTION_ERROR
            ? '-'
            : personalInformation?.birthDate ||
              data?.BIRTH_DATE ||
              getInfo(/(BIRTH|BIRTH)/, /PLACE/) ||
              '-',
        idText: 'birthday',
      },
      {
        title: 'birthPlace',
        value:
          status === StatusOperations.CONNECTION_ERROR ||
          idCapturedState.data?.BACKSIDE?.FIELD_DATA?.BIRTH_PLACE.length === 0
            ? '-'
            : `${
                personalInformation?.birthPlace ||
                idCapturedState.data?.BACKSIDE?.FIELD_DATA?.BIRTH_PLACE ||
                getInfo(/(BIRTH_PLACE|PLACE_OF_BIRTH)/) ||
                '-'
              }`,
        idText: 'birthplace',
      },
      {
        title: 'address',
        value:
          status === StatusOperations.CONNECTION_ERROR ||
          (!personalInformation?.address &&
            idCapturedState.data?.BACKSIDE?.FIELD_DATA?.ADDRESS.length === 0)
            ? '-'
            : `${
                personalInformation?.address ||
                idCapturedState.data?.BACKSIDE?.FIELD_DATA?.ADDRESS.join(
                  ', '
                ) ||
                getInfo(/ADDRESS/) ||
                '-'
              }`,
        idText: 'address',
      },
      {
        title: 'city',
        value:
          status === StatusOperations.CONNECTION_ERROR ||
          idCapturedState.data?.BACKSIDE?.FIELD_DATA?.BIRTH_PLACE.length === 0
            ? '-'
            : `${
                personalInformation?.birthPlace ||
                idCapturedState.data?.BACKSIDE?.FIELD_DATA?.BIRTH_PLACE ||
                getInfo(/(BIRTH_PLACE|PLACE_OF_BIRTH|CITY)/) ||
                '-'
              }`,
        idText: 'city',
      },
    ];
  };

  const resultIcon = (
    statusType?: StatusTypeProps,
    negative?: boolean,
    iconId?: string
  ) => {
    const canadaMorphological = statusType === StatusOperations.CHECK_PLATFORM;
    return canadaMorphological || !statusType ? null : (
      <StatusIcon
        status={statusType}
        invert={negative}
        testId={iconId ? iconId : 'stausIcon'}
      />
    );
  };

  useEffect(() => {
    if (appTesting) {
      connectSSE(newOperationId);
    }
  }, []);

  useEffect(() => {
    const assetsList = opreationAssets.map((item, index) => (
      <ResultsDataImg
        key={index}
        bigImg={item.type !== DOCUMENT_SELFIE && item.type !== DOCUMENT_FACIAL}
        id={`result_obtained_images_asset_${index + 1}`}
        data-test={`result_obtained_images_asset_${index + 1}`}
      >
        <ImageLoad asset={item} />
        <Label
          fontSize="12"
          id={`result_obtained_images_label_asset_${index + 1}`}
          testId={`result_obtained_images_label_asset_${index + 1}`}
        >
          {t(item.type)}
        </Label>
      </ResultsDataImg>
    ));
    setUnifiedAssets(assetsList);
  }, [opreationAssets]);

  useEffect(() => {
    if (civilRegistryState?.data?.result === COUNTRY_NOT_SUPPORTED)
      setShowGovernmentValidation(false);
  }, [civilRegistryState]);

  useEffect(() => {
    if (!timeoutError && !timeoutId.current && !appTesting)
      timeoutId.current = setTimeout(() => {
        setIsOperationFinished(true);
        disconnect();
      }, FINISH_TIMEOUT);

    if (
      operationFinishedState.status !== StatusOperations.STARTED ||
      timeoutError
    )
      cleanTimeout();
  }, [operationFinishedState.status, timeoutError]);

  useEffect(() => {
    return () => {
      cleanTimeout();
    };
  }, []);

  return (
    <ResultsWrapper flexDirection="column" rowGap="1.6">
      <ResultsHeader />
      {canReloadResults && (
        <ResultsButton
          color="primary"
          variant="filled"
          size="L"
          onClick={handleReload}
          testId="results_onboarding_reload_button"
        >
          {t('Reload results')}
        </ResultsButton>
      )}
      {canCheckPlatform && (
        <ResultsButton
          color="primary"
          variant="filled"
          size="L"
          onClick={handleVisitPlatform}
          testId="results_onboarding_reload_button"
        >
          {t('Check results on platform')}
        </ResultsButton>
      )}
      <CardResults
        title={t('BIOMETRIC VALIDATION') as string}
        // TODO: Adjust this to real data from SSE
        // dropdown={renderFacialResultListDropdown()}
        idTitle="result_biometric_validation_title"
      >
        <CardResultsContent
          flexDirection="column"
          alignItems={isMobile ? 'start' : 'center'}
          rowGap="1.6"
        >
          <Label
            fontSize="21"
            testId="result_biometric_validation_subtitle"
            id="result_biometric_validation_subtitle"
            semibold
            upperCase
          >
            {t('FACE VERIFICATION')}
          </Label>
          <SelfieValidations
            loading={biometricValidationLoading}
            assets={selfieValidationAssets}
            facialState={facialAuthenticationState.data?.authStatus}
            provider={internalProviders.demoBackend}
            livenessState={passiveLivenessState?.status}
            operationStatus={operationFinishedState.data?.status}
            antispoofValidation={antispoofResult}
            idLabelSelphi="result_matching_photo_selphi_label"
            idLabelPhotoDocument="result_matching_photo_document_label"
            idMatchingResult="result_matching_section_text"
            idMatchingSelphi="result_matching_photo_selphi"
            idMatchingPhotoDocument="result_matching_photo_document"
            idResultMatchingIcon="result_matching_icon"
          />
        </CardResultsContent>
      </CardResults>
      <ResultsDataWrapper
        flexDirection={isMobile ? 'column' : 'row'}
        columnGap="1.6"
        rowGap="1.6"
      >
        <CardList
          title={t('Obtained data')}
          subTitle={
            idCapturedState?.loading
              ? `${t('VALIDATION_IN_PROGRESS')}`
              : idCapturedState?.data?.BACKSIDE.MRZ_DATA
              ? `${idCapturedState?.data?.BACKSIDE.MRZ_DATA.NAME} ${idCapturedState?.data?.BACKSIDE.MRZ_DATA.SURNAME}`
              : `${t('SERVICE NOT AVAILABLE')}`
          }
          data={transformPersonalInformation(
            idCapturedState.status || StatusOperations.CONNECTION_ERROR
          )}
          isRow
          loading={idCapturedState?.loading}
          testId="obtained-data"
          idTitle="results_obtained_data_title"
          idSubtitle="results_obtained_data_full_name_value"
          icon={
            idCapturedState?.status === StatusOperations.CONNECTION_ERROR ||
            idCapturedState?.status === StatusOperations.EXPIRED
              ? {
                  iconName: circleIconData['warning'].iconName,
                  iconColor: circleIconData['warning'].iconColor,
                }
              : null
          }
          cardId="results_obtained_data"
          iconResultId="results_obtained_data_error_connection_icon"
        />
        <CardResults
          title={t('IMAGES OBTAINED') as string}
          idTitle="result_obtained_images_title"
        >
          <CardResultsContent>
            <ResultsDataImgWrapper
              wrap={isMobile ? 'no-wrap' : 'wrap'}
              justifyContent="flex-start"
              alignItems="flex-start"
              testId="images-obtained"
            >
              {unifiedAssets
                ? unifiedAssets
                : operationFinishedState.loading
                ? getLoadingDocuments()
                : null}
            </ResultsDataImgWrapper>
          </CardResultsContent>
          {logImages && (
            <FlexContainer justifyContent="flex-end">
              <ButtonLinkStyles
                variant="text"
                color="primary"
                size="S"
                semiBold={false}
                onClick={downloadImages}
                testId="result_obtained_images_download"
              >
                {t('Download images')}
              </ButtonLinkStyles>
            </FlexContainer>
          )}
        </CardResults>
      </ResultsDataWrapper>
      <GridContainer gap="1.6" gridTemplateColumns={isMobile ? '' : '1fr 1fr'}>
        {/* FRONT&BACK CORRESPONDENCE */}
        <CardGridSmall
          title={t('FRONT&BACK CORRESPONDENCE') as string}
          gridRow="auto/span 5"
          gridColumn="1"
          alignSelf={'stretch'}
          iconStatus={resultIcon(correspondenceSidesState, false)}
          infobarIcon={['IdentificationCard', 'IdentificationCardBackIcon']}
          message="DOCUMENT SIDES"
          icon={undefined}
          status={correspondenceSidesState}
          loading={
            idCapturedState.loading && !correspondenceSidesSetted.current
          }
          testId="results_correspondence_doc_card"
          titleId="results_correspondence_doc_title_text"
          messageId="results_correspondence_doc_subtitle_text"
          iconStatusId="results_correspondence_result_checker_image"
          infoBarId="results_correspondence_doc_footer"
          infoBarTextId="correspondence_doc_checker_text"
          infoBarIconId="results_correspondence_doc_back_image"
        />
        {/* OCR: EXTRACTED DATA */}
        <CardGridSmall
          title={t('READING OF THE DOCUMENT') as string}
          gridRow="auto/span 5"
          gridColumn="2"
          alignSelf={'stretch'}
          order={''}
          navigateTo={generatePath(RoutesName.ocr, {
            operationId,
          })}
          message="Extracted data"
          icon="IdentificationCardBackIcon"
          loading={idCapturedState.loading}
          testId="results_ocr_card"
          linkId="results_ocr_card_footer_text"
          messageId="results_ocr_card_subtitle"
          iconId="results_ocr_card_service_icon"
          titleId="results_ocr_card_title"
          infoBarId="card-grid-info-bar"
        />
        {/* PreviousOnboardings */}
        {previousOnboardingsValidation && (
          <CardGridSmall
            title={t('SECURITY') as string}
            gridRow="auto/span 5"
            iconStatus={resultIcon(previousOnboardingState?.status, false)}
            // TODO: ADD WHEN THE DETAIL PAGE IS DONE
            // navigateTo={generatePath(RoutesName.previousOnboardings, {
            //   operationId,
            // })}
            message="Previous onboarding"
            icon="UserCircle"
            loading={previousOnboardingState?.loading}
            status={previousOnboardingState?.status}
            testId="previousOnboardings-card"
            infoBarId={previousOnboardingState?.status}
            linkId="results_previous_onboarding_card_footer_see_more_text"
            messageId="results_previous_onboarding_card_subtitle"
            iconId="results_previous_onboarding_card_image"
            titleId="results_previous_onboarding_card_title"
            infoBarTextId="results_previous_onboarding_card_footer_text"
            iconStatusId="results_previous_onboarding_card_icon"
          />
        )}
        {/* Antifraud TO DO WHEN IS DONE */}
        {/* {antifraud && (
          <CardGridSmall
            // TODO: ADAPT TO THE CORRESPONDING EVENT
            title={t('SECURITY') as string}
            gridRow="auto/span 5"
            iconStatus={resultIcon(idValidatedState?.status, false)}
            message="BLOCKED LISTS"
            icon="XCircle"
            loading={idValidatedState?.loading}
            status={idValidatedState?.status}
            testId="antifraud-card"
            titleId="results_blocked_list_card_title"
            messageId="results_blocked_list_card_subtitle"
            iconStatusId="results_blocked_list_card_icon"
            iconId="results_blocked_list_card_image"
            infoBarTextId="results_blocked_list_card_footer_text"
          />
        )} */}
        {/* Government Services */}
        {showGovernmentValidation && (
          <CardGridSmall
            message="Government"
            gridRow="auto/span 5"
            alignSelf={'stretch'}
            iconStatus={resultIcon(civilRegistryState?.status, false)}
            title={t('SECURITY') as string}
            icon={governementServiceIcon}
            loading={civilRegistryState?.loading}
            navigateTo={generatePath(RoutesName.government, {
              operationId,
            })}
            status={civilRegistryState?.status}
            testId="governmentServices"
            titleId="results_governmental_card_title"
            messageId="results_governmental_card_subtitle"
            iconStatusId="results_governmental_card_icon"
            iconId="results_governmental_card_image"
            infoBarTextId="results_governmental_card_footer_text"
            linkId="results_governmental_card_footer_see_more_text"
          />
        )}
        {/* Morphological validation */}
        {documentValidation && (
          <CardGridSmall
            title={t('SECURITY') as string}
            gridRow="auto/span 5"
            alignSelf={'stretch'}
            iconStatus={resultIcon(idValidatedState?.status, false)}
            navigateTo={
              !isCanada
                ? generatePath(RoutesName.security, {
                    operationId,
                  })
                : ''
            }
            message={isCanada ? canadaMorphologicalMessage : 'Morphological'}
            icon="LockKey"
            loading={idValidatedState?.loading}
            status={idValidatedState?.status}
            testId="morphologicalValidation-card"
            infoBarId={idValidatedState?.status}
            titleId="results_morphology_card_title"
            messageId="results_morphology_card_subtitle"
            iconStatusId="results_morphology_card_icon"
            iconId="results_morphology_card_image"
            infoBarTextId="results_morphology_card_footer_text"
            linkId="results_morphology_card_footer_see_more_text"
          />
        )}
        {/* Document Signature */}
        {viewSignButton && (
          <CardGridSmall
            title={t('Signature').toUpperCase()}
            gridRow="auto/span 5"
            iconStatus={resultIcon(
              operationFinishedState?.loading || documentSignatureState.loading
                ? StatusTypeList.IN_PROGRESS
                : documentSignatureStatus,
              false
            )}
            message="Digital document"
            icon="FileText"
            loading={
              documentSignatureState.loading || operationFinishedState?.loading
            }
            status={documentSignatureStatus}
            testId="documentSignature-card"
            titleId="results_signature_card_title"
            messageId="results_signature_card_subtitle"
            iconStatusId="results_signature_card_icon"
            iconId="results_signature_card_image"
            infoBarTextId="results_signature_card_footer_text"
          />
        )}
      </GridContainer>
      <ResultsButton
        color="primary"
        variant={outlineFinishButton ? 'outline' : 'filled'}
        $transparent={outlineFinishButton}
        size="L"
        onClick={handleFinish}
        testId="results_finish_button"
      >
        {t('Finish')}
      </ResultsButton>
      {viewSignButton && (
        <>
          <ResultsButton
            color="primary"
            size="L"
            upperCase
            onClick={() => {
              setShowModal(true);
            }}
            testId="results_signature_document_button"
            disabled={operationFinishedState.loading || disableSignButton}
          >
            {t('sign document')}
          </ResultsButton>
          <ModalSign
            show={showModal}
            onChangeShow={(showModal) => setShowModal(showModal)}
            onCancel={() => setShowModal(false)}
            signatureAttemps={signatureAttemps}
            setSignatureAttemps={setSignatureAttemps}
            signType={signType}
          />
        </>
      )}
    </ResultsWrapper>
  );
};
